import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, styled } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PasswordTextField from './TextFields/PasswordTextField';
import { NODE_UI } from '../../NodeUI/nodeUiStyles';

const Title = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '6px',
  userSelect: 'none',
}));

const INSTRUCTION_LINE_HEIGHT = 1.5;

const InstructionBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: NODE_UI.spacing.sm,
  padding: '8px 10px',
  borderRadius: NODE_UI.borderRadiusSm,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fafafa',
  marginBottom: NODE_UI.spacing.md,
}));

const InstructionIconWrap = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  height: `calc(${NODE_UI.fontSize.caption} * ${INSTRUCTION_LINE_HEIGHT})`,
}));

const InstructionIcon = styled(InfoOutlinedIcon)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
  display: 'block',
}));

const InstructionText = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  lineHeight: INSTRUCTION_LINE_HEIGHT,
  margin: 0,
}));

const InstructionLink = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'none',
  fontWeight: 500,
  wordBreak: 'break-all',
  '&:hover': { textDecoration: 'underline' },
}));

const StepRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  marginTop: '6px',
}));

const StepNumber = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  fontWeight: 600,
  color: theme.palette.text.secondary,
  minWidth: '16px',
  lineHeight: 1.5,
}));

const StepText = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.primary,
  lineHeight: 1.5,
  flex: 1,
}));

const WarningBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 10px',
  borderRadius: NODE_UI.borderRadiusSm,
  border: `1px solid ${theme.palette.warning.light}`,
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.1)' : '#fffbeb',
  marginTop: NODE_UI.spacing.sm,
}));

const WarningText = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.warning.dark,
  lineHeight: 1.4,
}));

const ApiKeySecretsField = ({
  label,
  instructionText,
  instructions,
  instructionLink,
  secretFieldName,
  value,
  onChange,
  placeholder,
  errorMessage,
  credentialStatus,
  showPasswordField,
  hasInteractedWithSecret,
  onShowPasswordField,
  onInteractedWithSecret,
  warningText,
}) => {
  const handleSecretValueChange = (fieldKey, val) => {
    onChange(val);
    onInteractedWithSecret(true);
  };

  const hasSteps = instructions && instructions.length > 0;

  return (
    <Box>
      <Title>{label}</Title>
      {(instructionText || (instructions && instructions.length > 0)) && (
        <InstructionBox sx={hasSteps ? undefined : { alignItems: 'center' }}>
          <InstructionIconWrap>
            <InstructionIcon />
          </InstructionIconWrap>
          <Box flex={1}>
            {instructionText && (
              <InstructionText>
                {instructionText}{' '}
                {instructionLink && (
                  <InstructionLink
                    href={instructionLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {instructionLink}
                  </InstructionLink>
                )}
              </InstructionText>
            )}
            {instructions && instructions.length > 0 && (
              <Box mt={instructionText ? 1 : 0}>
                {instructions.map((step, i) => (
                  <StepRow key={i}>
                    <StepNumber>{i + 1}.</StepNumber>
                    <StepText>{step}</StepText>
                  </StepRow>
                ))}
              </Box>
            )}
          </Box>
        </InstructionBox>
      )}

      <PasswordTextField
        value={value || ''}
        onChange={(field, val) => handleSecretValueChange(secretFieldName, val)}
        placeholder={placeholder}
        errorCondition={!value || value.trim() === ''}
        errorMessage={errorMessage}
        fieldKey={secretFieldName}
        credentialStatus={credentialStatus}
        showPasswordField={showPasswordField}
        hasInteractedWithSecret={hasInteractedWithSecret}
        onShowPasswordField={onShowPasswordField}
        onInteractedWithSecret={onInteractedWithSecret}
      />

      {warningText && (
        <WarningBox>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'warning.dark' }} />
          <WarningText>{warningText}</WarningText>
        </WarningBox>
      )}
    </Box>
  );
};

ApiKeySecretsField.propTypes = {
  label: PropTypes.string.isRequired,
  instructionText: PropTypes.string,
  instructions: PropTypes.arrayOf(PropTypes.string),
  instructionLink: PropTypes.string,
  secretFieldName: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  errorMessage: PropTypes.string.isRequired,
  credentialStatus: PropTypes.string.isRequired,
  showPasswordField: PropTypes.bool.isRequired,
  hasInteractedWithSecret: PropTypes.bool,
  onShowPasswordField: PropTypes.func.isRequired,
  onInteractedWithSecret: PropTypes.func.isRequired,
  warningText: PropTypes.string,
};

export default ApiKeySecretsField;
